"use client";

import React, { useState, useEffect, useRef } from "react";
import type { SkillInfo } from "./HighlightOverlay";
import { getCaretCoordinates } from "@/lib/textarea-caret";
import { getSkillIcon } from "./SkillIcons";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import { FilesChevronRightIcon } from "@/components/icons/FilesChevronRightIcon";

const DROPDOWN_DIVIDER_STYLE: React.CSSProperties = {
  borderBottom: "1px solid var(--mention-divider)",
};

interface SkillDropdownProps {
  open?: boolean;
  skills: SkillInfo[];
  /** The element that has focus while the person types: the list hears its
   *  arrow / Enter / Tab keys, and measures its caret when it is a textarea. */
  anchorRef: React.RefObject<HTMLElement | null>;
  triggerPos?: number;
  /** For an editor that is NOT a textarea (the pill's rich editor): the caret's
   *  rect, already shaped for the popover (the editor's left and width, the
   *  caret's top and height; rich-composer/caret.ts). Absent, a textarea anchor
   *  is measured exactly as it always was. */
  caretRect?: () => DOMRect | null;
  zIndex?: string;
  onSelect: (skill: SkillInfo) => void;
  onClose: () => void;
}

export function SkillDropdown({
  open = true,
  skills,
  anchorRef,
  triggerPos,
  caretRect,
  zIndex,
  onSelect,
  onClose,
}: SkillDropdownProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset selection when skills change or dropdown opens
  useEffect(() => {
    setSelectedIndex(0);
  }, [skills, open]);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (open && listRef.current) {
      const selectedItem = listRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedIndex, open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open || !anchorRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // An Enter that COMMITS an IME composition is not a pick: with pinyin or
      // romaji after the trigger the letters being composed filter the list,
      // and picking there rewrites the editor under a live composition. The
      // legacy 229 covers engines that leave isComposing unset.
      if (e.isComposing || e.keyCode === 229) return;
      if (skills.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev < skills.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : skills.length - 1));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        onSelect(skills[selectedIndex]);
      }
    };

    const el = anchorRef.current;
    el.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      el.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open, skills, selectedIndex, anchorRef, onSelect]);

  return (
    <Popover
      open={open && skills.length > 0}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <PopoverAnchor
        virtualRef={{
          current: {
            getBoundingClientRect: () => {
              const fromHost = caretRect?.();
              if (fromHost) return fromHost;
              const anchor = anchorRef.current;
              if (
                anchor instanceof HTMLTextAreaElement &&
                typeof triggerPos === "number"
              ) {
                const caret = getCaretCoordinates(anchor, triggerPos);
                const textareaRect = anchor.getBoundingClientRect();
                return new DOMRect(
                  textareaRect.left,
                  caret.top,
                  textareaRect.width,
                  caret.height
                );
              }
              return anchor?.getBoundingClientRect() || new DOMRect(0, 0, 0, 0);
            },
          },
        }}
      />
      <PopoverContent
        className={`skill-dropdown w-(--radix-popover-trigger-width) ${zIndex} p-0`}
        align="start"
        side="bottom"
        sideOffset={8}
        role="listbox"
        aria-label="Skill suggestions"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div
          className="px-4 py-3 flex items-center gap-2"
          style={DROPDOWN_DIVIDER_STYLE}
        >
          <div className="w-5 h-5 rounded-8 flex items-center justify-center bg-fg-default">
            <span className="font-bold text-bg-base text-2.5">/</span>
          </div>
          <span
            className="text-3.5 font-medium tracking-wide uppercase"
            style={{ color: "var(--mention-header-text)" }}
          >
            Run Skill
          </span>
        </div>

        {/* Skill List */}
        <div
          ref={listRef}
          className="py-2 max-h-64 overflow-y-auto overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {skills.map((skill, index) => {
            const isSelected = index === selectedIndex;

            return (
              <button
                key={skill.id}
                data-index={index}
                onClick={() => onSelect(skill)}
                onMouseEnter={() => setSelectedIndex(index)}
                onPointerDown={(e) => e.preventDefault()}
                role="option"
                aria-selected={isSelected}
                className="w-full text-left px-3 py-2.5 mx-1 rounded-16 transition-colors duration-150"
                style={{
                  width: "calc(100% - 8px)",
                  background: isSelected
                    ? "var(--mention-item-selected-bg)"
                    : "transparent",
                  border: isSelected
                    ? "1px solid var(--mention-item-selected-border)"
                    : "1px solid transparent",
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-16 flex items-center justify-center flex-shrink-0"
                    style={{
                      background: "var(--mention-icon-bg)",
                      border: "1px solid var(--mention-icon-border)",
                      color: skill.color || "#ad1a72",
                    }}
                  >
                    {getSkillIcon(skill.icon)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-3.5 font-semibold text-[#ad1a72]">
                        /{skill.slug}
                      </span>
                      <span
                        className="text-2.5 font-medium px-1.5 py-0.5 rounded-8 uppercase tracking-wider"
                        style={{
                          background: "var(--mention-badge-bg)",
                          color: "var(--mention-badge-text)",
                        }}
                      >
                        {skill.category}
                      </span>
                    </div>
                    <p
                      className="skill-desc text-3.5 mt-0.5 truncate"
                      style={{ color: "var(--mention-description)" }}
                    >
                      {skill.description}
                    </p>
                  </div>

                  {isSelected && (
                    <div className="w-6 h-6 rounded-12 flex items-center justify-center flex-shrink-0 bg-bg-muted">
                      <FilesChevronRightIcon
                        width={12}
                        height={12}
                        stroke="var(--fg-default)"
                        strokeWidth={3}
                      />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer Hint */}
        <div
          className="px-4 py-2.5 hidden can-hover:flex items-center justify-between"
          style={{
            borderTop: "1px solid var(--mention-divider)",
            background: "var(--mention-footer-bg)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <kbd
                className="px-1.5 py-0.5 rounded text-2.5 font-mono text-4"
                style={{
                  background: "var(--mention-kbd-bg)",
                  color: "var(--mention-kbd-text)",
                }}
              >
                up
              </kbd>
              <kbd
                className="px-1.5 py-0.5 rounded text-2.5 font-mono text-4"
                style={{
                  background: "var(--mention-kbd-bg)",
                  color: "var(--mention-kbd-text)",
                }}
              >
                down
              </kbd>
              <span
                className="text-2.5 ml-1"
                style={{ color: "var(--mention-hint-text)" }}
              >
                navigate
              </span>
            </div>
            <div className="flex items-center gap-1">
              <kbd
                className="px-1.5 py-0.5 rounded text-2.5 font-mono text-4"
                style={{
                  background: "var(--mention-kbd-bg)",
                  color: "var(--mention-kbd-text)",
                }}
              >
                enter
              </kbd>
              <span
                className="text-2.5 ml-1"
                style={{ color: "var(--mention-hint-text)" }}
              >
                select
              </span>
            </div>
          </div>
          <span
            className="text-2.5"
            style={{ color: "var(--mention-hint-muted)" }}
          >
            esc to close
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
